import axios from "axios";
import { bundleNow, isNetError, loadBundle, noteNetError, noteNetOk } from "./offlineStore";
import { signHeaders, unlockList, userIdFromToken } from "./geoLock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;
const TOKEN_KEY = "xpattern.token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));
// vehicle → patterns promise, so re-picking or hovering a vehicle is instant (cleared on any upload / delete)
const patternCache = new Map();
const PATTERN_TTL = 5 * 60 * 1000;
axios.interceptors.request.use((cfg) => { const t = getToken(); if (t && cfg.url?.startsWith(API)) cfg.headers.Authorization = `Bearer ${t}`; return cfg; });
// app-only API: every call carries a signature the server checks on geometry endpoints (scripts / curl / replays get 403)
axios.interceptors.request.use(async (cfg) => {
  if (!cfg.url?.startsWith(API)) return cfg;
  const t = getToken();
  Object.assign(cfg.headers, await signHeaders(cfg.method || "get", new URL(cfg.url).pathname, t || ""));
  return cfg;
});
// locked outlines → plain points the moment they arrive (the rest of the app never sees `geo`)
export const unlockPatterns = (list) => unlockList(list, userIdFromToken(getToken() || ""));
let refreshing = null;
// any write that can change library geometry drops the pattern cache
axios.interceptors.response.use((r) => { const m = (r.config?.method || "get").toLowerCase(); if (m !== "get" && /\/(patterns|templates|import|vehicles|library|inspect)/.test(r.config?.url || "")) patternCache.clear(); return r; });
axios.interceptors.response.use((r) => r, async (err) => {
  const cfg = err.config;
  if (err.response?.status === 401 && cfg && !cfg._retry && !cfg.url.includes("/auth/")) {
    cfg._retry = true;
    try {
      refreshing = refreshing || axios.post(`${API}/auth/refresh`).then((r) => r.data.access_token).finally(() => { refreshing = null; });
      const t = await refreshing;
      setToken(t);
      return axios(cfg);
    } catch (e2) { setToken(null); window.dispatchEvent(new CustomEvent("xpattern:signed-out", { detail: e2?.response?.data?.detail || err.response?.data?.detail || "" })); }
  }
  // Idempotent GETs: retry twice with jitter on network errors, 5xx and 429 (honouring Retry-After up to 5 s).
  // Gateway errors (502/503/504/52x = the server is restarting after a publish) get longer, more patient retries, and the
  // safe POSTs (sign-in, presence ping, refresh) join in so a restart never surfaces as a raw Cloudflare error to a customer.
  const st = err.response?.status;
  const gateway = !err.response || st === 502 || st === 503 || st === 504 || (st >= 520 && st <= 524);
  const safePost = SAFE_POSTS.some((u) => cfg?.url?.includes(u));
  const isGet = cfg && (cfg.method || "get").toLowerCase() === "get";
  const max = gateway ? 4 : 2;
  const retriable = cfg && (isGet || (safePost && gateway)) && (!err.response || st >= 500 || st === 429) && (cfg._retries || 0) < max;
  if (retriable) {
    cfg._retries = (cfg._retries || 0) + 1;
    const ra = Number(err.response?.headers?.["retry-after"]);
    const wait = st === 429 && ra ? Math.min(ra, 5) * 1000 : gateway ? Math.min(8000, 1500 * cfg._retries) + Math.random() * 500 : 400 * cfg._retries + Math.random() * 400;
    await new Promise((r) => setTimeout(r, wait));
    return axios(cfg);
  }
  if (isNetError(err)) noteNetError();
  return Promise.reject(err);
});
axios.interceptors.response.use((r) => { noteNetOk(); return r; });
// Offline mode (owner-enabled per customer): when the server cannot be reached AT ALL and this laptop holds a valid offline
// bundle, these reads answer from it. Any other error (401/403/5xx…) passes through untouched, so online behaviour is unchanged.
const offlineFallback = (call, pick) => async (...args) => {
  try { return await call(...args); }
  catch (e) { if (!isNetError(e)) throw e; await loadBundle(); const b = bundleNow(); if (!b) throw e; const v = pick(b, ...args); if (v === undefined) throw e; return v; }
};
const SAFE_POSTS = ["/auth/login", "/auth/ping", "/auth/refresh", "/auth/otp/request"];
const RESTARTING = "Xpattern is restarting after an update — please try again in a moment.";
export const apiError = (e, fallback = "Something went wrong") => {
  const st = e?.response?.status, body = e?.response?.data;
  if (st === 502 || st === 503 || st === 504 || (st >= 520 && st <= 524) || (typeof body === "string" && /<html|cloudflare/i.test(body))) return RESTARTING;
  const d = body?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return d?.msg || e?.message || fallback;
};
const g = (url, params) => axios.get(`${API}${url}`, { params }).then((r) => r.data);
// Authenticated file download: fetches as a blob and saves it under the server's file name. Resolves {name, headers}.
const download = async (url, params) => {
  const r = await axios.get(`${API}${url}`, { params, responseType: "blob" });
  const cd = r.headers["content-disposition"] || "";
  const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
  const plain = cd.match(/filename="?([^";]+)"?/i);
  const name = star ? decodeURIComponent(star[1]) : plain ? plain[1] : "download";
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  return { name, headers: r.headers };
};
const p = (url, body) => axios.post(`${API}${url}`, body).then((r) => r.data);
const put = (url, body) => axios.put(`${API}${url}`, body).then((r) => r.data);

export const api = {
  getWrapPresets: () => g("/wrap-presets"),
  putWrapPreset: (patternId, body) => put(`/wrap-presets/${patternId}`, body),
  deleteWrapPreset: (patternId) => axios.delete(`${API}/wrap-presets/${patternId}`).then((r) => r.data),
  register: (body) => p("/auth/register", body),
  login: (body) => p("/auth/login", body),
  otpRequest: (email, purpose, name = "") => p("/auth/otp/request", { email, purpose, name }),
  refreshSession: () => axios.post(`${API}/auth/refresh`).then((r) => r.data),
  otpVerify: (email, code) => p("/auth/otp/verify", { email, code }),
  setPassword: (password, current_password = "") => p("/auth/password", { password, current_password }),
  passwordLater: () => p("/auth/password/later", {}),
  resetPassword: (email, code, password) => p("/auth/password/reset", { email, code, password }),
  paymentsConfig: () => g("/payments/config"),
  subscribe: (plan, offerToken) => p("/payments/subscribe", { plan, offer_token: offerToken || null }),
  offerByToken: (token) => g(`/offers/${encodeURIComponent(token)}`),
  acceptOffer: (token) => p(`/offers/${encodeURIComponent(token)}/accept`, {}),
  adminOffer: (id, body) => p(`/admin/customers/${id}/offer`, body),
  adminOfferRecord: (id, body) => p(`/admin/customers/${id}/offer/record`, body),
  adminOfferCancel: (id) => axios.delete(`${API}/admin/customers/${id}/offer`).then((r) => r.data),
  adminOffersBulk: (body) => p("/admin/offers/bulk", body),
  adminOffersSend: (body) => p("/admin/offers/send", body),
  cleanOutline: (points) => p("/geometry/clean", { points }),
  matchSides: (left, right, mode = "check") => p("/geometry/match-sides", { left, right, mode }),
  curveTest: (machine_id, settings) => p("/plotter/curve-test", { machine_id, settings }),
  warmCut: (outlines) => p("/plotter/warm", { outlines }),
  downloads: () => g("/downloads"),
  macDmgUrl: () => `${API}/downloads/Xpattern.dmg`,
  curveAudit: () => g("/admin/curve-audit"),
  doctor: (left, right = null, fix = true) => p("/geometry/doctor", { left, right, fix }),
  libraryExport: () => g("/admin/library/export"),
  libraryImport: (file) => p("/admin/library/import", file),
  libraryTotals: () => g("/admin/library/totals"),
  buyerLinkCreate: (body) => p("/admin/library/links", body),
  buyerLinks: (vehicle_id = "") => g("/admin/library/links", { vehicle_id }),
  buyerLinkRevoke: (token) => axios.delete(`${API}/admin/library/links/${encodeURIComponent(token)}`).then((r) => r.data),
  buyerPage: (token) => g(`/files/${encodeURIComponent(token)}`),
  buyerZipUrl: (token) => `${API}/files/${encodeURIComponent(token)}/zip`,
  buyerPreviewUrl: (token, pid) => `${API}/files/${encodeURIComponent(token)}/preview/${pid}.png`,
  libraryTotalsCsv: () => download("/admin/library/totals.csv"),
  vehicleFilesZip: (vehicleId, service = "Tint") => download(`/admin/library/files/vehicle/${vehicleId}`, { service }),
  libraryFilesZip: (service = "Tint") => download("/admin/library/files/all", { service }),
  libraryHealth: () => g("/admin/library-health"),
  libraryHealthStart: () => p("/admin/library-health/start"),
  librarySquareUp: () => p("/admin/library-health/square-up?force=true"),
  libraryHealthFix: (pattern_ids = [], all = false) => p("/admin/library-health/fix", { pattern_ids, all }),
  nameFixesScan: (vehicle_id = "") => g(`/admin/library/name-fixes${vehicle_id ? `?vehicle_id=${vehicle_id}` : ""}`),
  nameFixesApply: (vehicle_id = "", pattern_ids = []) => p("/admin/library/name-fixes/apply", { vehicle_id, pattern_ids }),
  sidesStart: (types = "", vehicle_id = "") => p(`/admin/library/sides/start?types=${encodeURIComponent(types)}&vehicle_id=${vehicle_id}`),
  sidesStatus: () => g("/admin/library/sides"),
  sidesApply: (items) => p("/admin/library/sides/apply", { items }),
  coreDotGet: () => g("/admin/library/core-dot"),
  coreDotSet: (enabled) => put("/admin/library/core-dot", { enabled }),
  coreDotRescan: () => p("/admin/library/core-dot/rescan"),
  subscribeConfirm: (subscription_id, autopay = true) => p("/payments/subscribe/confirm", { subscription_id, autopay }),
  adminMultiComputer: (id, enabled) => p(`/admin/customers/${id}/multi-computer`, { enabled }),
  adminComp: (id, on) => p(`/admin/customers/${id}/comp`, { on }),
  adminXshop: (id, on) => p(`/admin/customers/${id}/xshop`, { on }),
  shopSettings: () => g("/shop/settings"),
  shopSaveSettings: (b) => put("/shop/settings", b),
  shopSchedule: (start, end) => g("/shop/schedule", { start, end }),
  shopLeads: () => g("/shop/leads"),
  shopCreateLead: (b) => p("/shop/leads", b),
  shopPatchLead: (id, b) => axios.patch(`${API}/shop/leads/${id}`, b).then((r) => r.data),
  shopDeleteLead: (id) => axios.delete(`${API}/shop/leads/${id}`).then((r) => r.data),
  shopConvertLead: (id) => p(`/shop/leads/${id}/convert`),
  shopPayStatus: () => g("/shop/payments/status"),
  shopPayConnect: () => p("/shop/payments/connect"),
  shopPaySync: () => p("/shop/payments/sync"),
  shopPayLink: (id, kind, amount) => p(`/shop/jobs/${id}/pay-link`, { kind, amount }),
  shopContacts: (params) => g("/shop/contacts", params),
  shopContact: (id) => g(`/shop/contacts/${id}`),
  shopCreateContact: (b) => p("/shop/contacts", b),
  shopPatchContact: (id, b) => axios.patch(`${API}/shop/contacts/${id}`, b).then((r) => r.data),
  shopDeleteContact: (id) => axios.delete(`${API}/shop/contacts/${id}`).then((r) => r.data),
  shopContactNote: (id, text) => p(`/shop/contacts/${id}/notes`, { text }),
  shopSyncContacts: () => p("/shop/contacts/sync"),
  shopDashboard: () => g("/shop/dashboard", { tz_offset: new Date().getTimezoneOffset() }),
  shopSignJob: (id, name, kind) => p(`/shop/jobs/${id}/sign`, { name, kind }),
  shopUploadLogo: (file) => { const fd = new FormData(); fd.append("file", file); return axios.post(`${API}/shop/logo`, fd).then((r) => r.data); },
  shopDeleteLogo: () => axios.delete(`${API}/shop/logo`).then((r) => r.data),
  shopEstimates: (params) => g("/shop/estimates", params),
  shopEstimate: (id) => g(`/shop/estimates/${id}`),
  shopCreateEstimate: (b) => p("/shop/estimates", b),
  shopPatchEstimate: (id, b) => axios.patch(`${API}/shop/estimates/${id}`, b).then((r) => r.data),
  shopDeleteEstimate: (id) => axios.delete(`${API}/shop/estimates/${id}`).then((r) => r.data),
  shopSendEstimate: (id) => p(`/shop/estimates/${id}/send`),
  shopAcceptEstimate: (id, b) => p(`/shop/estimates/${id}/accept`, b),
  shopConvertEstimate: (id, b) => p(`/shop/estimates/${id}/convert`, b || {}),
  shopDuplicateEstimate: (id) => p(`/shop/estimates/${id}/duplicate`),
  shopEstimatePdfUrl: (id) => `${API}/shop/estimates/${id}/pdf`,
  shopInvoices: (params) => g("/shop/invoices", params),
  shopInvoiceStats: () => g("/shop/invoices/stats"),
  shopInvoice: (id) => g(`/shop/invoices/${id}`),
  shopCreateInvoice: (b) => p("/shop/invoices", b),
  shopInvoiceFromJob: (jobId) => p(`/shop/invoices/from-job/${jobId}`),
  shopPatchInvoice: (id, b) => axios.patch(`${API}/shop/invoices/${id}`, b).then((r) => r.data),
  shopDeleteInvoice: (id) => axios.delete(`${API}/shop/invoices/${id}`).then((r) => r.data),
  shopVoidInvoice: (id) => p(`/shop/invoices/${id}/void`),
  shopSendInvoice: (id) => p(`/shop/invoices/${id}/send`),
  shopInvoicePayment: (id, b) => p(`/shop/invoices/${id}/payments`, b),
  shopRemoveInvoicePayment: (id, idx) => axios.delete(`${API}/shop/invoices/${id}/payments/${idx}`).then((r) => r.data),
  shopInvoicePayLink: (id) => p(`/shop/invoices/${id}/pay-link`),
  shopInvoicePdfUrl: (id) => `${API}/shop/invoices/${id}/pdf`,
  shopInventory: () => g("/shop/inventory"),
  shopCreateRoll: (b) => p("/shop/inventory/rolls", b),
  shopPatchRoll: (id, b) => axios.patch(`${API}/shop/inventory/rolls/${id}`, b).then((r) => r.data),
  shopDeleteRoll: (id) => axios.delete(`${API}/shop/inventory/rolls/${id}`).then((r) => r.data),
  shopRestockRoll: (id, b) => p(`/shop/inventory/rolls/${id}/restock`, b),
  shopFilmUsage: (b) => p("/shop/inventory/usage", b),
  shopRemoveFilmUsage: (id) => axios.delete(`${API}/shop/inventory/usage/${id}`).then((r) => r.data),
  shopFilmSuggest: (jobId) => g(`/shop/inventory/suggest/${jobId}`),
  shopReviews: () => g("/shop/reviews"),
  shopReviewSettings: (b) => put("/shop/reviews/settings", b),
  shopReviewForJob: (jobId) => p(`/shop/reviews/job/${jobId}`),
  shopReviewSend: (id) => p(`/shop/reviews/${id}/send`),
  shopReviewCancel: (id) => p(`/shop/reviews/${id}/cancel`),
  shopReviewDelete: (id) => axios.delete(`${API}/shop/reviews/${id}`).then((r) => r.data),
  shopLeadForm: () => g("/shop/leadform"),
  shopSaveLeadForm: (b) => put("/shop/leadform", b),
  publicBookForm: (slug) => axios.get(`${API}/public/book/${slug}`).then((r) => r.data),
  publicBookSubmit: (slug, b) => axios.post(`${API}/public/book/${slug}`, b).then((r) => r.data),
  shopAnalytics: (months = 6) => g("/shop/analytics", { months, tz_offset: new Date().getTimezoneOffset() }),
  publicEstimate: (token) => g(`/public/estimates/${token}`),
  publicAcceptEstimate: (token, b) => p(`/public/estimates/${token}/accept`, b),
  publicDeclineEstimate: (token, reason) => p(`/public/estimates/${token}/decline`, { reason }),
  publicEstimateDeposit: (token) => p(`/public/estimates/${token}/deposit-link`),
  publicEstimatePdfUrl: (token) => `${API}/public/estimates/${token}/pdf`,
  publicInvoice: (token) => g(`/public/invoices/${token}`),
  publicInvoicePay: (token) => p(`/public/invoices/${token}/pay`),
  publicInvoicePdfUrl: (token) => `${API}/public/invoices/${token}/pdf`,
  paySession: (sid) => g(`/pay/session/${sid}`),
  adminCutSettings: (id, settings, machine_id = "") => p(`/admin/customers/${id}/cut-settings`, { settings, machine_id }),
  adminServices: (id, services) => p(`/admin/customers/${id}/services`, { services }),
  adminUnlock: (id, service, on) => p(`/admin/customers/${id}/unlock`, { service, on }),
  promoClick: (code) => p("/promos/click", { code }),
  promoApply: (code, plan) => p("/promos/apply", { code, plan }),
  promoMine: () => g("/promos/mine"),
  adminPromos: () => g("/admin/promos"),
  adminPromoCreate: (body) => p("/admin/promos", body),
  adminPromoUpdate: (id, body) => axios.put(`${API}/admin/promos/${id}`, body).then((r) => r.data),
  adminPromoActive: (id, active) => p(`/admin/promos/${id}/active`, { active }),
  adminPromoDelete: (id) => axios.delete(`${API}/admin/promos/${id}`).then((r) => r.data),
  adminPromoCustomers: (id) => g(`/admin/promos/${id}/customers`),
  adminPromoPay: (id, amount, note) => p(`/admin/promos/${id}/pay`, { amount, note }),
  adminPromoAdjust: (id, amount, note) => p(`/admin/promos/${id}/adjust`, { amount, note }),
  adminSetPromo: (id, code) => p(`/admin/customers/${id}/promo`, { code }),
  adminPurgeInactive: () => p("/admin/purge-inactive"),
  adminFleet: () => g("/admin/fleet"),
  adminRemoteAction: (id, action, message = "") => p(`/admin/fleet/${id}/action`, { action, message }),
  adminRemoteActions: (id) => g(`/admin/fleet/${id}/actions`),
  relayNoticeAck: (code, id) => p(`/relay/${code}/notice/${id}/ack`),
  adminMonitor: () => g("/admin/monitor"),
  adminScrapeGuard: () => g("/admin/scrape-guard"),
  adminScrapeLimits: (body) => put("/admin/scrape-guard/limits", body),
  adminFreeze: (id, frozen) => p(`/admin/customers/${id}/freeze`, { frozen }),
  adminMonitorClear: () => axios.delete(`${API}/admin/monitor/incidents`).then((r) => r.data),
  adminSystem: (days = 7) => g(`/admin/system?days=${days}`),
  adminErrorResolve: (key, resolved = true) => p("/admin/errors/resolve", { key, resolved }),
  adminErrorDetail: (key) => g(`/admin/errors/${encodeURIComponent(key)}`),
  logout: () => p("/auth/logout"),
  me: offlineFallback(() => g("/auth/me"), (b) => ({ ...b.user, offline_session: true })),
  previewLogin: () => p("/auth/preview"),
  signOutEverywhere: () => p("/auth/sign-out-everywhere"),
  honorAck: () => p("/auth/honor/ack"),
  ping: () => p("/auth/ping"),
  checkout: (plan = "monthly") => p("/payments/checkout", { origin_url: window.location.origin, plan, autopay: plan !== "onetime" }),
  paymentStatus: (sid) => g(`/payments/status/${sid}`),
  adminCustomers: () => g("/admin/customers"),
  adminPresence: () => g("/admin/presence"),
  cancelSubscription: () => p("/payments/cancel", {}),
  resumeSubscription: () => p("/payments/resume", {}),
  adminReviewer: () => p("/admin/reviewer"),
  adminRecoverCustomer: (email) => p("/admin/customers/recover", { email }),
  adminRemovedCustomers: () => g("/admin/customers/removed"),
  adminRestoreCustomer: (query, trialDays = 0) => p("/admin/customers/restore", { query, trial_days: trialDays }),
  adminStripeMigrationPlan: () => g("/admin/stripe/migration"),
  adminStripeMigrationApply: () => p("/admin/stripe/migration/apply"),
  adminRenameCustomer: (id, shop_name, name, email = "") => axios.patch(`${API}/admin/customers/${id}/name`, { shop_name, name, email }).then((r) => r.data),
  adminSetCountry: (id, country) => axios.patch(`${API}/admin/customers/${id}/country`, { country }).then((r) => r.data),
  introVideo: () => axios.get(`${API}/intro-video`).then((r) => r.data),
  introVideoDismiss: () => axios.post(`${API}/intro-video/dismiss`).then((r) => r.data),
  introVideoView: (body) => axios.post(`${API}/intro-video/view`, body).then((r) => r.data),
  adminIntroVideoStats: () => axios.get(`${API}/admin/intro-video/stats`).then((r) => r.data),
  introVideoUrl: () => `${API}/intro-video/stream?token=${encodeURIComponent(getToken() || "")}`,
  adminIntroVideoChunk: (uploadId, index, blob, final, filename, title) => axios.post(`${API}/admin/intro-video/upload`, blob, { params: { upload_id: uploadId, index, final, filename, title }, headers: { "Content-Type": "application/octet-stream" }, timeout: 120000 }).then((r) => r.data),
  adminIntroVideoTitle: (title) => axios.patch(`${API}/admin/intro-video`, { title }).then((r) => r.data),
  adminIntroVideoDelete: () => axios.delete(`${API}/admin/intro-video`).then((r) => r.data),
  adminTrends: () => g("/admin/trends"),
  adminExtend: (id, days) => p(`/admin/customers/${id}/extend`, { days }),
  adminFollowup: (id) => p(`/admin/customers/${id}/followup`, {}),
  adminDiscount: (id, body) => p(`/admin/customers/${id}/discount`, body),
  adminFreeMonth: (id) => p(`/admin/customers/${id}/free-month`, {}),
  adminDiscountCancel: (id) => axios.delete(`${API}/admin/customers/${id}/discount`).then((r) => r.data),
  adminImpersonate: (id) => p(`/admin/customers/${id}/impersonate`),
  adminDemoExtendUndo: (id) => p(`/admin/demos/${id}/extend/undo`),
  adminDemoConvert: (id, days = 0) => p(`/admin/demos/${id}/convert`, { days }),
  adminActivate: (id) => p(`/admin/customers/${id}/activate`),
  adminDeactivate: (id) => p(`/admin/customers/${id}/deactivate`),
  adminSuspend: (id, suspended) => p(`/admin/customers/${id}/suspend`, { suspended }),
  adminSecurity: () => g("/admin/security"),
  adminTemplateAccess: () => g("/admin/template-access"),
  adminSignOutUser: (id) => p(`/admin/customers/${id}/sign-out`),
  adminIdentifyLeak: (file) => { const fd = new FormData(); fd.append("file", file); return p("/admin/identify-leak", fd); },
  acceptTerms: () => p("/auth/accept-terms"),
  adminUnbind: (id) => axios.delete(`${API}/admin/customers/${id}/devices`).then((r) => r.data),
  adminRemoveCustomer: (id) => axios.delete(`${API}/admin/customers/${id}`).then((r) => r.data),
  adminRequests: () => g("/admin/requests"),
  adminRequestDone: (body) => p("/admin/requests/done", body),
  adminRequestUnavailable: (body) => p("/admin/requests/unavailable", body),
  adminRequestReopen: (body) => p("/admin/requests/reopen", body),
  adminRequestDelete: (body) => p("/admin/requests/delete", body),
  adminRequestsClear: (body) => p("/admin/requests/clear", body),
  adminContact: () => g("/admin/contact"),
  adminContactRead: (id) => p(`/admin/contact/${id}/read`),
  adminContactDelete: (id) => axios.delete(`${API}/admin/contact/${id}`).then((r) => r.data),
  adminInboxDelete: (id) => axios.delete(`${API}/admin/inbox/${id}`).then((r) => r.data),
  adminInbox: () => g("/admin/inbox"),
  adminInboxPoll: () => p("/admin/inbox/poll"),
  adminInboxRead: (id) => p(`/admin/inbox/${id}/read`),
  adminInboxReply: (id, body) => p(`/admin/inbox/${id}/reply`, { body }),
  adminUnlockTrial: (id) => p(`/admin/customers/${id}/unlock-trial`),
  adminEmails: () => g("/admin/emails"),
  adminEmailTemplates: () => g("/admin/emails/templates"),
  adminEmailTemplateSend: (kind) => p(`/admin/emails/templates/${kind}/send`),
  adminRunReminders: () => p("/admin/reminders/run"),
  requestVehicle: (body) => p("/requests", body),
  services: () => g("/services"),
  adminSetService: (service, live) => put("/admin/services", { service, live }),
  adminSetHubTab: (tab, shown) => put("/admin/hub-tabs", { tab, shown }),
  adminSetHubOrder: (order) => put("/admin/hub-order", { order }),
  adminSetIntroScene: (scene) => put("/admin/intro-scene", { scene }),
  flatJobs: () => g("/flat-jobs"),
  saveFlatJob: (body) => p("/flat-jobs", body),
  deleteFlatJob: (id) => axios.delete(`${API}/flat-jobs/${id}`).then((r) => r.data),
  decals: () => g("/decals"),
  uploadDecals: (files) => { const fd = new FormData(); files.forEach((f) => fd.append("files", f)); return axios.post(`${API}/decals/upload`, fd).then((r) => r.data); },
  renameDecal: (id, name) => axios.patch(`${API}/decals/${id}`, { name }).then((r) => r.data),
  deleteDecal: (id) => axios.delete(`${API}/decals/${id}`).then((r) => r.data),
  deleteDecals: (ids) => p("/decals/delete", { ids }),
  lettering: (body) => p("/decals/lettering", body),
  myRequests: () => g("/requests/mine"),
  ticketCategories: () => g("/tickets/categories"),
  createTicket: (body) => p("/tickets", body),
  myTickets: () => g("/tickets/mine"),
  myTicketUnread: () => g("/tickets/unread"),
  replyTicket: (id, body) => p(`/tickets/${id}/reply`, body),
  seenTicket: (id) => p(`/tickets/${id}/seen`, {}),
  adminTickets: (status) => g(`/admin/tickets${status ? `?status=${status}` : ""}`),
  adminTicketReply: (id, body) => p(`/admin/tickets/${id}/reply`, body),
  adminTicketStatus: (id, status) => p(`/admin/tickets/${id}/status`, { status }),
  adminTicketSeen: (id) => p(`/admin/tickets/${id}/seen`, {}),
  leaveFeedback: (body) => p("/feedback", body),
  myFeedback: () => g("/feedback/mine"),
  adminFeedback: () => g("/admin/feedback"),
  demoInfo: (code) => g(`/demo/${code}`),
  demoActivate: (code) => p(`/demo/${code}/activate`, {}),
  demoAck: () => p("/demo/ack", {}),
  adminDemos: () => g("/admin/demos"),
  shopJobs: (params) => g("/shop/jobs", params),
  shopSummary: () => g("/shop/summary"),
  shopCreateJob: (body) => p("/shop/jobs", body),
  shopPatchJob: (id, body) => axios.patch(`${API}/shop/jobs/${id}`, body).then((r) => r.data),
  shopDeleteJob: (id) => axios.delete(`${API}/shop/jobs/${id}`).then((r) => r.data),
  shopEmailJob: (id, kind) => p(`/shop/jobs/${id}/email`, { kind }),
  adminInvites: () => g("/admin/invites"),
  adminSendInvite: (body) => p("/admin/invites", body),
  adminResendInvite: (id) => p(`/admin/invites/${id}/resend`),
  adminDeleteInvite: (id) => axios.delete(`${API}/admin/invites/${id}`).then((r) => r.data),
  adminAutopay: () => g("/admin/autopay"),
  adminDemoCreate: (body) => p("/admin/demos", body),
  adminDemoDeactivate: (id) => p(`/admin/demos/${id}/deactivate`, {}),
  adminDemoExtend: (id, days) => p(`/admin/demos/${id}/extend`, { days }),
  adminDemoRebuild: (id) => p(`/admin/demos/${id}/rebuild`, {}),
  adminDemoInvite: (id) => p(`/admin/demos/${id}/invite`, {}),
  adminDemoFollowup: (id) => p(`/admin/demos/${id}/followup`, {}),
  adminDemoApprove: (id) => p(`/admin/demos/${id}/approve`, {}),
  adminDemoDelete: (id) => axios.delete(`${API}/admin/demos/${id}`).then((r) => r.data),
  makes: () => g("/vehicles/makes"),
  vehicles: offlineFallback((params) => g("/vehicles", params), (b) => b.vehicles.map((v) => v.vehicle)),
  vehiclePhoto: (id) => g(`/vehicles/${id}/photo`),
  replaceVehiclePhoto: (id, file) => { const fd = new FormData(); fd.append("file", file); return axios.post(`${API}/admin/vehicles/${id}/photo`, fd).then((r) => r.data); },
  retryVehiclePhoto: (id) => p(`/admin/vehicles/${id}/photo/retry`),
  resetVehiclePhoto: (id) => axios.delete(`${API}/admin/vehicles/${id}/photo`).then((r) => r.data),
  libraryVersion: () => g("/library/version"),
  catalog: () => g("/catalog"),
  importKits: (params) => g("/import/kits", params),
  sizeSuggest: (params) => g("/import/size-suggest", params),
  despeckServiceLibrary: (service) => p(`/admin/library/${service}/despeck`),
  retraceServiceLibrary: (service) => p(`/admin/library/${service}/retrace`),
  inspectJob: (jobId) => g(`/import/inspect/${jobId}`),
  movePpfOutOfTint: (apply) => axios.post(`${API}/admin/library/move-to-ppf`, null, { params: { apply } }).then((r) => r.data),
  libraryTrash: () => g("/admin/library/trash"),
  libraryPairCheck: () => g("/admin/library/pair-check"),
  libraryFixPairs: (apply = true) => p(`/admin/library/fix-pairs?apply=${apply}`),
  libraryCoreRetraceCount: (software = "CORE") => g(`/admin/library/core-retrace?software=${encodeURIComponent(software)}`),
  addVehicleTrim: (id, body) => p(`/vehicles/${id}/trims`, body),
  libraryCoreRetrace: (vehicleIds, software = "CORE") => p("/admin/library/core-retrace", { vehicle_ids: vehicleIds || [], software }),
  libraryCornerCheck: (vehicleId = "", minDev = 0) => g("/admin/library/corner-check", { vehicle_id: vehicleId || undefined, min_dev: minDev }),
  libraryRoundCorners: (apply = false, vehicleId = "", force = false) => g("/admin/library/round-corners", { apply: apply ? 1 : 0, vehicle_id: vehicleId || undefined, force: force ? 1 : undefined }),
  libraryRoundCornersStatus: () => g("/admin/library/round-corners/status"),
  libraryTopEdges: (apply = false, vehicleId = "", force = false) => g("/admin/library/top-edges", { apply: apply ? 1 : 0, vehicle_id: vehicleId || undefined, force: force ? 1 : undefined }),
  libraryTopEdgesStatus: () => g("/admin/library/top-edges/status"),
  libraryDigicutTwins: (apply = false, vehicleId = "") => g("/admin/library/digicut-twins", { apply: apply ? 1 : 0, vehicle_id: vehicleId || undefined }),
  libraryDigicutTwinsApply: (vehicleIds) => p("/admin/library/digicut-twins/apply", { vehicle_ids: vehicleIds }),
  libraryDigicutTwinsStatus: () => g("/admin/library/digicut-twins/status"),
  libraryJobCancel: (jobId) => p(`/admin/library/jobs/${jobId}/cancel`, {}),
  libraryJobsRunning: () => g("/admin/library/jobs/running"),
  libraryJobLast: (kind) => g(`/admin/library/jobs/last?kind=${encodeURIComponent(kind)}`),
  libraryDigicutTwinsCsv: (jobId) => download(`/admin/library/digicut-twins/${jobId}/report.csv`),
  patternRoundCorners: (patternId, radius = 1 / 64) => p(`/patterns/${patternId}/round-corners`, { radius }),
  libraryFixCorners: (vehicleIds, minDev = 0) => p("/admin/library/fix-corners", { vehicle_ids: vehicleIds || [], min_dev: minDev }),
  libraryTrashRestore: (id, vehicle) => p(`/admin/library/trash/${id}/restore`, vehicle || undefined),
  mergeDuplicateVehicles: (apply) => axios.post(`${API}/admin/vehicles/merge-duplicates`, null, { params: { apply } }).then((r) => r.data),
  moveKitsToPpf: (vehicleId, apply) => axios.post(`${API}/admin/vehicles/${vehicleId}/move-to-ppf`, null, { params: { apply } }).then((r) => r.data),
  clearServiceLibrary: (service) => axios.delete(`${API}/admin/library/${service}`).then((r) => r.data),
  catalogHidden: () => g("/catalog/hidden"),
  adminCatalogHide: (make, model, year = null) => p("/admin/catalog/hide", { make, model, year }),
  adminCatalogUnhide: (make, model, year = null) => p("/admin/catalog/unhide", { make, model, year }),
  adminCatalogStatus: () => g("/admin/catalog/status"),
  adminCatalogAddMake: (make, model = null) => p("/admin/catalog/make", { make, model }),
  adminCatalogSync: () => p("/admin/catalog/sync?force=true", {}),
  adminConsolidateSets: (dryRun = true) => p(`/admin/library/consolidate-sets?dry_run=${dryRun}`, {}),
  patterns: (vehicleId) => {
    const hit = patternCache.get(vehicleId);
    if (hit && Date.now() - hit.at < PATTERN_TTL) return hit.p;
    const p = g(`/vehicles/${vehicleId}/patterns`).catch(async (e) => { patternCache.delete(vehicleId); if (isNetError(e)) { await loadBundle(); const hit2 = bundleNow()?.vehicles.find((v) => v.vehicle.id === vehicleId); if (hit2) return hit2.patterns; } throw e; }).then(unlockPatterns);
    patternCache.set(vehicleId, { at: Date.now(), p });
    p.then((list) => { if (Array.isArray(list) && list.some((x) => x.clean_pending)) patternCache.delete(vehicleId); }).catch(() => {});  // outlines still on the old clean-up: ask again next time, don't pin them for 5 min
    return p;
  },
  prefetchPatterns: (vehicleId) => { if (vehicleId) api.patterns(vehicleId).catch(() => {}); },
  invalidatePatterns: (vehicleId) => (vehicleId ? patternCache.delete(vehicleId) : patternCache.clear()),
  layouts: (vehicleId) => g("/layouts", { vehicle_id: vehicleId }),
  saveLayout: (body) => p("/layouts", body),
  deleteLayout: (id) => axios.delete(`${API}/layouts/${id}`).then((r) => r.data),
  versions: (patternId) => g(`/patterns/${patternId}/versions`),
  saveVersion: (patternId, body) => p(`/patterns/${patternId}/versions`, body),
  rollback: (patternId, version) => p(`/patterns/${patternId}/rollback/${version}`),
  publishers: () => g("/publishers"),
  machines: offlineFallback(() => g("/machines"), (b) => b.machines),
  jobs: () => g("/jobs"),
  job: (id) => g(`/jobs/${id}`),
  createJob: (body) => p("/jobs", body),
  sendJob: (id, report) => p(`/jobs/${id}/send`, report),
  bridgeScriptUrl: (code) => `${API}/bridge/xpattern_bridge.py?code=${encodeURIComponent(code || "")}`,
  bridgeBatUrl: (code) => `${API}/bridge/Xpattern-Bridge.bat?code=${encodeURIComponent(code || "")}`,
  setupBatUrl: (code) => `${API}/bridge/Xpattern-Setup.bat?code=${encodeURIComponent(code || "")}`,
  setupMacUrl: (code) => `${API}/bridge/Xpattern-Setup.command?code=${encodeURIComponent(code || "")}`,
  relayStatus: (code) => g(`/relay/${code}/status`),
  relayQueue: (code) => g(`/relay/${code}/queue`),
  relayQueueClear: (code, target) => p(`/relay/${code}/queue/clear`, { target }),
  relayCreateJob: (code, body) => p(`/relay/${code}/jobs`, body),
  relayJob: (code, id, wait) => g(`/relay/${code}/jobs/${id}`, { wait }),
  commandsUrl: (id) => `${API}/jobs/${id}/commands`,
  deleteVehicle: (id, service) => axios.delete(`${API}/vehicles/${id}`, { params: service ? { service } : {} }).then((r) => r.data),
  updateVehicleYears: (id, body) => axios.patch(`${API}/vehicles/${id}/years`, body).then((r) => r.data),
  adminRenameModel: (vehicleIds, model) => axios.post(`${API}/admin/library/rename-model`, { vehicle_ids: vehicleIds, model }).then((r) => r.data),
  adminDuplicateModel: (vehicleIds, model) => axios.post(`${API}/admin/library/duplicate-model`, { vehicle_ids: vehicleIds, model }).then((r) => r.data),
  adminMergeVehicles: (fromId, intoId) => axios.post(`${API}/admin/library/merge-vehicles`, { from_id: fromId, into_id: intoId }).then((r) => r.data),
  adminUnmergeVehicles: (undo) => axios.post(`${API}/admin/library/merge-vehicles/undo`, { undo }).then((r) => r.data),
  bodyStyles: () => axios.get(`${API}/body-styles`).then((r) => r.data.styles),
  adminAddBodyStyle: (name) => axios.post(`${API}/admin/body-styles`, { name }).then((r) => r.data),
  adminRemoveBodyStyle: (name) => axios.delete(`${API}/admin/body-styles/${encodeURIComponent(name)}`).then((r) => r.data),
  updateVehicleGroups: (id, body) => axios.patch(`${API}/vehicles/${id}/groups`, body).then((r) => r.data),
  updateVehicleGlass: (id, body) => axios.patch(`${API}/vehicles/${id}/glass`, body).then((r) => r.data),
  deletePattern: (id) => axios.delete(`${API}/patterns/${id}`).then((r) => r.data),
  renamePattern: (id, name) => axios.patch(`${API}/patterns/${id}`, { name }).then((r) => r.data),
  claimConnector: (code) => p("/relay/claim", { code }),
  contact: (body) => p("/contact", body),
  decodeVin: (vin) => g(`/vin/${vin}`),
  updateProfile: (body) => axios.patch(`${API}/auth/me`, body).then((r) => r.data),
  plotters: offlineFallback(() => g("/plotters"), (b) => ({ plotters: b.plotters, cut_settings: b.cut_settings, calibration: b.calibration, prefs: b.prefs })),
  offlineBundle: () => g("/offline/bundle"),
  offlinePin: (vehicleId) => p(`/offline/pins/${vehicleId}`),
  offlineUnpin: (vehicleId) => axios.delete(`${API}/offline/pins/${vehicleId}`).then((r) => r.data),
  offlineJobsUpload: (jobs) => p("/offline/jobs", jobs),
  adminOffline: (id, body) => p(`/admin/customers/${id}/offline`, body),
  saveCalibration: (cal) => put("/plotters/calibration", cal),
  saveCutSettings: (body) => put("/plotters/cut-settings", body),
  savePrefs: (body) => put("/plotters/prefs", body),
  addPlotter: (body) => p("/plotters", body),
  activatePlotter: (id) => p(`/plotters/${id}/activate`),
  updatePlotter: (id, body) => axios.patch(`${API}/plotters/${id}`, body).then((r) => r.data),
  testSquare: (body) => p("/jobs/test-square", body),
  wrap: (body) => p("/decals/wrap", body),
  userVehicles: () => g("/decals/vehicles"),
  userVehicleUpload: (file, name, length) => { const fd = new FormData(); fd.append("photo", file); fd.append("name", name || ""); fd.append("length", length || 190); return axios.post(`${API}/decals/vehicles`, fd).then((r) => r.data); },
  userVehicleUpdate: (id, body) => axios.patch(`${API}/decals/vehicles/${id}`, body).then((r) => r.data),
  userVehicleDelete: (id) => axios.delete(`${API}/decals/vehicles/${id}`).then((r) => r.data),
  removePlotter: (id) => axios.delete(`${API}/plotters/${id}`).then((r) => r.data),
  // Sticker Studio
  stickerArt: () => g("/stickers/art"),
  uploadStickerArt: (file) => { const fd = new FormData(); fd.append("file", file); return axios.post(`${API}/stickers/art`, fd).then((r) => r.data); },
  deleteStickerArt: (id) => axios.delete(`${API}/stickers/art/${id}`).then((r) => r.data),
  stickerKeyBg: (id, tol = 28) => { const fd = new FormData(); fd.append("tol", tol); return axios.post(`${API}/stickers/art/${id}/key`, fd).then((r) => r.data); },
  stickerRemoveBg: (id) => p(`/stickers/art/${id}/remove-bg`, {}),
  stickerAiArt: (body) => p("/stickers/ai-art", body),
  stickerContour: (blob, ppi, offset, bridge) => { const fd = new FormData(); fd.append("mask", blob, "mask.png"); fd.append("ppi", ppi); fd.append("offset", offset); fd.append("bridge", bridge); return axios.post(`${API}/stickers/contour`, fd).then((r) => r.data); },
  stickerPdf: (blob, w, h) => { const fd = new FormData(); fd.append("png", blob, "sheet.png"); fd.append("w", w); fd.append("h", h); return axios.post(`${API}/stickers/pdf`, fd, { responseType: "blob" }).then((r) => r.data); },
  stickerDesigns: () => g("/stickers/designs"),
  saveStickerDesign: (id, body) => put(`/stickers/designs/${id}`, body),
  deleteStickerDesign: (id) => axios.delete(`${API}/stickers/designs/${id}`).then((r) => r.data),
};
